import { Show, type Component } from "solid-js";

/** Стиль аватара. Соответствует `User.avatar_style` (enum в Django). */
export type AvatarStyle = "INITIALS" | "MALE" | "FEMALE";

interface Props {
  name?: string | null;
  email?: string | null;
  /** Стиль кружка: буквы / мужской силуэт / женский силуэт. По умолчанию — буквы. */
  style?: AvatarStyle | null;
  /** Прямой URL картинки. На странице Профиля и в шапке сейчас не используется
   *  (см. data_model.md::User), но оставлен для случая водительского PWA. */
  src?: string | null;
  size?: number;
  color?: string;
}

const HUES = [205, 162, 30, 280, 350, 100];

function initials(name?: string | null, email?: string | null): string {
  if (name && name.trim()) {
    return name
      .trim()
      .split(/\s+/)
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return "?";
}

function hashColor(seed: string): string {
  let acc = 0;
  for (let i = 0; i < seed.length; i++) acc += seed.charCodeAt(i);
  const hue = HUES[acc % HUES.length]!;
  return `oklch(0.55 0.12 ${hue})`;
}

/** Простой мужской силуэт (плечи + голова) на тёмно-синем фоне. */
const MaleSilhouette: Component<{ size: number; title?: string }> = (p) => (
  <svg
    viewBox="0 0 40 40"
    width={p.size}
    height={p.size}
    style={{ "border-radius": "50%", "flex-shrink": "0", display: "block" }}
    aria-label={p.title}
  >
    <title>{p.title}</title>
    <rect width="40" height="40" fill="oklch(0.55 0.12 230)" />
    <circle cx="20" cy="15" r="6" fill="white" />
    <path
      d="M8 36 C8 27 13 22 20 22 C27 22 32 27 32 36 Z"
      fill="white"
    />
  </svg>
);

/** Женский силуэт: круг головы + трапеция «волосы/плечи» на розово-фуксиевом фоне. */
const FemaleSilhouette: Component<{ size: number; title?: string }> = (p) => (
  <svg
    viewBox="0 0 40 40"
    width={p.size}
    height={p.size}
    style={{ "border-radius": "50%", "flex-shrink": "0", display: "block" }}
    aria-label={p.title}
  >
    <title>{p.title}</title>
    <rect width="40" height="40" fill="oklch(0.6 0.13 0)" />
    <circle cx="20" cy="14" r="6" fill="white" />
    {/* Плечи и каре-причёска — расширены по бокам. */}
    <path
      d="M6 36 C6 26 12 22 20 22 C28 22 34 26 34 36 Z"
      fill="white"
    />
    <path
      d="M13 13 C13 9 16 7 20 7 C24 7 27 9 27 13 L27 17 L24 16 C24 13 22 11 20 11 C18 11 16 13 16 16 L13 17 Z"
      fill="white"
      opacity="0.75"
    />
  </svg>
);

export const Avatar: Component<Props> = (props) => {
  const size = () => props.size ?? 28;
  const seed = () => props.name || props.email || "?";
  const bg = () => props.color ?? hashColor(seed());
  const title = () => props.name ?? props.email ?? undefined;
  const style = () => props.style ?? "INITIALS";

  return (
    <Show
      when={!props.src}
      fallback={
        <img
          src={props.src!}
          alt={props.name ?? props.email ?? "Аватар"}
          title={title()}
          style={{
            width: `${size()}px`,
            height: `${size()}px`,
            "border-radius": "50%",
            "object-fit": "cover",
            "flex-shrink": "0",
          }}
        />
      }
    >
      <Show when={style() === "MALE"}>
        <MaleSilhouette size={size()} title={title()} />
      </Show>
      <Show when={style() === "FEMALE"}>
        <FemaleSilhouette size={size()} title={title()} />
      </Show>
      <Show when={style() === "INITIALS"}>
        <div
          style={{
            width: `${size()}px`,
            height: `${size()}px`,
            "border-radius": "50%",
            background: bg(),
            color: "white",
            display: "inline-flex",
            "align-items": "center",
            "justify-content": "center",
            "font-size": `${size() * 0.4}px`,
            "font-weight": "700",
            "flex-shrink": "0",
          }}
          title={title()}
        >
          {initials(props.name, props.email)}
        </div>
      </Show>
    </Show>
  );
};
